import { Injectable } from 'framework/di';
import { Signal, signal } from 'framework/store/signal';

import { DomHelper } from '../../helpers/dom.helper';
import { appIntegration } from '../../integration';
import {
  buildProductDetailUrlWithLocale,
  handleProductSwatches,
  transformProductPrice,
} from '../../product-item';
import { handleTooltip } from '../../tooltip';
import { detectDeviceByWidth } from '../../utils';
import { AppService } from '../app/app.service';
import { CustomizationCompatibility } from '../backward-compatibility/customization.compatibility';
import { RecommendationModel } from './recommendation.model';
import { RecommendationService } from './recommendation.service';

/**
 * Controller for managing recommendation widget lifecycle, rendering, and carousel functionality.
 *
 * This controller orchestrates the complete lifecycle of recommendation widgets, from data fetching
 * to rendering and carousel initialization. It provides numerous extension points for Technical
 * Support teams to customize widget behavior for specific shops without modifying core code.
 *
 * @remarks
 * Key responsibilities include:
 * - Lazy loading widgets when they enter the viewport for performance
 * - Fetching recommendation data from the API
 * - Managing widget templates and rendering
 * - Configuring and initializing Slick carousel for carousel layouts
 * - Handling empty recommendation states
 *
 * Performance considerations:
 * - Uses IntersectionObserver for viewport detection to avoid unnecessary renders
 * - Lazy loads jQuery and Slick carousel only when carousel layout is needed
 * - Unobserves elements after rendering to prevent memory leaks
 *
 * @example
 * Extend to customize recommendation fetching with additional filters:
 * ```javascript
 * window.boostWidgetIntegration.extend('RecommendationWidgetController', (Base) => {
 *   return class CustomRecommendationController extends Base {
 *     protected async fetchRecommendationData(widgetId) {
 *       const model = await super.fetchRecommendationData(widgetId);
 *       if (model && this.customer?.tags?.includes('vip')) {
 *         // Filter products for VIP customers
 *         model.properties.products = model.properties.products.filter(
 *           p => p.tags?.includes('vip-eligible')
 *         );
 *       }
 *       return model;
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Extend to customize carousel configuration for specific shops:
 * ```javascript
 * window.boostWidgetIntegration.extend('RecommendationWidgetController', (Base) => {
 *   return class extends Base {
 *     protected getCarouselConfig(model) {
 *       const config = super.getCarouselConfig(model);
 *       // Customize for shop theme
 *       return {
 *         ...config,
 *         autoplay: true,
 *         autoplaySpeed: 5000,
 *         dots: false
 *       };
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class RecommendationWidgetController {
  protected template?: string;
  protected readonly MOBILE_BREAKPOINT: number = 768;

  /**
   * Reactive state management for the recommendation widget.
   *
   * Tracks loading status and the current recommendation model. The state
   * is observable and can be monitored for changes during the widget lifecycle.
   *
   * @remarks
   * This signal-based state management enables reactive UI updates and
   * is particularly useful when extending the controller to add custom
   * loading indicators or state-dependent behavior.
   */
  state: Signal<RecommendationWidgetControllerState>;

  /**
   * Reactive signal containing the widget's initialization properties.
   *
   * This signal is populated when the widget connects via the `connect()` method
   * and provides reactive access to the widget ID and other initialization props.
   * Useful for accessing widget configuration in extended methods.
   *
   * @remarks
   * The optional nature allows the controller to exist before connection.
   * Always check for existence before accessing in extension code.
   *
   * @example
   * Access widget ID in custom methods:
   * ```typescript
   * protected customMethod() {
   *   const widgetId = this.props?.value.widgetId;
   *   if (widgetId) {
   *     console.log(`Processing widget: ${widgetId}`);
   *   }
   * }
   * ```
   */
  props?: Signal<RecommendationWidgetControllerProps>;

  /**
   * Creates an instance of RecommendationWidgetController.
   *
   * @param domHelpers - Utility service for DOM manipulation and viewport observation
   * @param recommendationService - Service for fetching recommendation data and templates
   * @param appService - Core application service providing template rendering and configuration
   */
  constructor(
    protected domHelpers: DomHelper,
    protected recommendationService: RecommendationService,
    protected appService: AppService,
    protected customizationCompatibility: CustomizationCompatibility
  ) {
    this.state = signal({
      loading: false,
      model: null,
    });
  }

  /**
   * Initializes the recommendation widget and sets up viewport observation for lazy loading.
   *
   * This is the main entry point for widget initialization. It loads the widget template,
   * sets up an IntersectionObserver to detect when the widget enters the viewport, and
   * triggers rendering only when visible for optimal performance.
   *
   * @param props - Configuration properties for the widget
   * @param parent - The parent HTML element where the widget will be rendered
   *
   * @throws {Error} When template loading fails or parent element is invalid
   *
   * @remarks
   * The widget uses lazy loading strategy - it won't fetch data or render until
   * the parent element is visible in the viewport. This improves initial page load performance.
   *
   * @example
   * Basic usage:
   * ```typescript
   * const controller = container.resolve(RecommendationWidgetController);
   * const widgetElement = document.querySelector('#recommendation-widget');
   * await controller.connect({ widgetId: 'homepage-recommendations' }, widgetElement);
   * ```
   */
  async connect(props: RecommendationWidgetControllerProps, parent: HTMLElement) {
    this.props = signal(props);

    const { widgetId } = props;

    const template = await this.loadTemplate();
    this.template = template;

    const unobserve = this.domHelpers.observeElementInViewport(parent, async () => {
      await this.handleWidgetInViewport(widgetId, parent, unobserve);
    });
  }

  /**
   * Loads the widget template from the recommendation service.
   *
   * Override this method to customize template loading logic, such as loading
   * shop-specific templates, adding template preprocessing, or implementing
   * template caching strategies.
   *
   * @returns Promise resolving to the widget template HTML string
   *
   * @throws {Error} When template fetching fails or returns invalid data
   *
   * @example
   * Override to use custom template for specific shop:
   * ```typescript
   * protected async loadTemplate() {
   *   // Check if shop has custom template
   *   const customTemplate = await this.fetchCustomTemplate();
   *   if (customTemplate) {
   *     return customTemplate;
   *   }
   *   return super.loadTemplate();
   * }
   * ```
   */
  protected async loadTemplate(): Promise<string> {
    return this.recommendationService.getWidgetTemplate();
  }

  /**
   * Handles the complete widget lifecycle when it enters the viewport.
   *
   * This method orchestrates the entire widget rendering process: fetching data,
   * handling loading states, managing empty states, rendering the widget, and
   * cleaning up the observer. Override this method to customize the entire
   * widget lifecycle or add custom analytics, logging, or error handling.
   *
   * @param widgetId - The unique identifier of the widget to render
   * @param parent - The parent HTML element where the widget will be rendered
   * @param unobserve - Callback function to stop observing the element after rendering
   *
   * @throws {Error} When widget rendering fails due to network, template, or DOM errors
   *
   * @remarks
   * The method follows this lifecycle:
   * 1. Set loading state to true
   * 2. Fetch recommendation data
   * 3. Set loading state to false
   * 4. Handle empty recommendations (remove widget)
   * 5. Update state with model
   * 6. Render widget
   * 7. Execute post-render hooks
   * 8. Stop observing element
   *
   * Error handling: Errors during rendering will propagate to the caller.
   * Consider wrapping this method in a try-catch block when extending.
   *
   * @example
   * Override to add custom analytics tracking:
   * ```typescript
   * protected async handleWidgetInViewport(widgetId, parent, unobserve) {
   *   // Track widget impression
   *   this.analytics.trackImpression('recommendation_widget', { widgetId });
   *
   *   try {
   *     await super.handleWidgetInViewport(widgetId, parent, unobserve);
   *     this.analytics.trackSuccess('widget_rendered', { widgetId });
   *   } catch (error) {
   *     this.analytics.trackError('widget_failed', { widgetId, error });
   *     throw error;
   *   }
   * }
   * ```
   */
  protected async handleWidgetInViewport(
    widgetId: string,
    parent: HTMLElement,
    unobserve: () => void
  ): Promise<void> {
    const stateValue = this.state.value;
    stateValue.loading = true;

    const recommendationModel = await this.fetchRecommendationData(widgetId);
    stateValue.loading = false;

    this.state.value = {
      ...stateValue,
    };

    if (!recommendationModel || recommendationModel.properties.products.length === 0) {
      this.handleEmptyRecommendation(parent, unobserve);
      return;
    }

    stateValue.model = recommendationModel;

    this.state.value = {
      ...stateValue,
    };

    await this.renderWidget(recommendationModel, parent);

    this.afterWidgetRender();

    unobserve();
  }

  /**
   * Fetches recommendation data from the recommendation service.
   *
   * Override this method to add shop-specific data enrichment, modify API calls,
   * add caching strategies, or implement custom fallback logic when recommendations
   * are unavailable.
   *
   * @param widgetId - The unique identifier of the widget to fetch data for
   *
   * @returns Promise resolving to the recommendation model with widget configuration
   * and product data, or null if no recommendations are available or fetch fails
   *
   * @throws {Error} When API request fails or returns invalid data (caught internally, returns null)
   *
   * @example
   * Override to add product filtering based on customer segments:
   * ```typescript
   * protected async fetchRecommendationData(widgetId) {
   *   const model = await super.fetchRecommendationData(widgetId);
   *
   *   if (model && this.customer) {
   *     // Filter out products not available in customer's region
   *     model.properties.products = model.properties.products.filter(
   *       p => p.availableRegions?.includes(this.customer.region)
   *     );
   *   }
   *
   *   return model;
   * }
   * ```
   *
   * @example
   * Override to implement caching:
   * ```typescript
   * protected async fetchRecommendationData(widgetId) {
   *   const cached = this.cache.get(widgetId);
   *   if (cached && !this.cache.isExpired(widgetId)) {
   *     return cached;
   *   }
   *
   *   const model = await super.fetchRecommendationData(widgetId);
   *   if (model) {
   *     this.cache.set(widgetId, model, { ttl: 300 }); // 5 min cache
   *   }
   *   return model;
   * }
   * ```
   */
  protected async fetchRecommendationData(widgetId: string): Promise<RecommendationModel | null> {
    return (await this.recommendationService.getRecommendationModel(widgetId)) || null;
  }

  /**
   * Handles the case when recommendation data is empty or has no products.
   *
   * By default, removes the widget element from the DOM and stops observing.
   * Override this method to customize empty state behavior, such as showing
   * a fallback message, displaying alternative content, or tracking empty states.
   *
   * @param parent - The parent HTML element containing the widget
   * @param unobserve - Callback function to stop observing the element
   *
   * @example
   * Override to show fallback content instead of removing:
   * ```typescript
   * protected handleEmptyRecommendation(parent, unobserve) {
   *   parent.innerHTML = `
   *     <div class="no-recommendations">
   *       <p>Check back soon for personalized recommendations!</p>
   *     </div>
   *   `;
   *   unobserve();
   * }
   * ```
   *
   * @example
   * Override to track empty states for analytics:
   * ```typescript
   * protected handleEmptyRecommendation(parent, unobserve) {
   *   this.analytics.track('empty_recommendations', {
   *     widgetId: this.currentWidgetId,
   *     customerId: this.customer?.id
   *   });
   *   super.handleEmptyRecommendation(parent, unobserve);
   * }
   * ```
   */
  protected handleEmptyRecommendation(parent: HTMLElement, unobserve: () => void): void {
    parent.remove();
    unobserve();
  }

  /**
   * Renders the widget based on the model's layout configuration.
   *
   * Currently handles carousel layout rendering. Override this method to add support
   * for custom layouts, modify rendering logic, or add pre/post-render transformations.
   *
   * @param model - The recommendation model containing widget configuration and product data
   * @param parent - The parent HTML element where the widget will be rendered
   *
   * @remarks
   * The default implementation only renders carousel layouts. For grid or other layouts,
   * the template rendering is handled elsewhere in the widget lifecycle.
   *
   * @example
   * Override to add support for custom grid layout:
   * ```typescript
   * protected async renderWidget(model, parent) {
   *   const layout = model.properties.widgetDesignSettings?.layoutDisplay;
   *
   *   if (layout === 'carousel') {
   *     await this.renderCarouselWidget(parent);
   *   } else if (layout === 'masonry') {
   *     await this.renderMasonryWidget(parent);
   *   } else {
   *     await this.renderGridWidget(parent);
   *   }
   * }
   * ```
   */
  protected async renderWidget(model: RecommendationModel, parent: HTMLElement): Promise<void> {
    if (model.properties.widgetDesignSettings?.layoutDisplay === 'carousel') {
      await this.renderCarouselWidget(parent);
    }
  }

  /**
   * Called after widget rendering is complete.
   *
   * This lifecycle hook is executed after the widget has been fully rendered in the DOM.
   * It triggers app integration for cart buttons, executes legacy customization hooks
   * for backward compatibility, and other interactive elements.
   *
   * Override this method to add post-render logic like analytics tracking, custom event
   * handlers, third-party integrations, or accessibility enhancements.
   *
   * @remarks
   * The method performs the following in order:
   * 1. Triggers app integration (cart buttons, quick view, etc.)
   * 2. Executes legacy customization.afterRender hooks for backward compatibility
   *    - Allows existing shops with custom JS to continue working
   *    - Supports both general and recommendation-specific hooks
   *
   * @example
   * Override to add analytics tracking:
   * ```typescript
   * protected afterWidgetRender() {
   *   super.afterWidgetRender(); // Important: call super to maintain integrations
   *
   *   const model = this.state.value.model;
   *   if (model) {
   *     this.analytics.trackProductImpressions(
   *       model.properties.products.map(p => ({
   *         id: p.id,
   *         name: p.title,
   *         list: `Recommendations - ${model.properties.widgetId}`
   *       }))
   *     );
   *   }
   * }
   * ```
   *
   * @example
   * Override to add custom interactive features:
   * ```typescript
   * protected afterWidgetRender() {
   *   super.afterWidgetRender();
   *
   *   // Add quick view functionality
   *   document.querySelectorAll('.product-quick-view').forEach(btn => {
   *     btn.addEventListener('click', (e) => this.handleQuickView(e));
   *   });
   * }
   * ```
   */
  protected afterWidgetRender(): void {
    appIntegration();

    const widgetId = this.props?.value.widgetId;
    if (widgetId) {
      const blockId = `boost-sd-widget-${widgetId}`;
      this.customizationCompatibility.afterRender(blockId, 'recommendation');

      // Apply product price formatting, swatches, URLs, and tooltips
      const widgetElement = document.getElementById(widgetId);
      if (widgetElement) {
        const context = this.buildLegacyContext(widgetElement);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        transformProductPrice(context as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        handleProductSwatches(context as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        buildProductDetailUrlWithLocale(context as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        handleTooltip(context as any);
      }
    }
  }

  /**
   * Builds a legacy context object for compatibility with product-item helpers.
   *
   * This method creates a minimal Context object required by legacy helper functions.
   * The helper functions will extract additional settings from DOM attributes and
   * global configuration as needed.
   *
   * @param widgetElement - The widget DOM element
   * @returns Minimal context object with document and element references
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private buildLegacyContext(widgetElement: HTMLElement): Record<string, any> {
    const taeConfig = this.appService.TAEAppConfig;

    return {
      $element: widgetElement,
      document: document,
      blockType: 'recommendation',
      app: {
        ...taeConfig,
      },
    };
  }

  /**
   * Gets the mobile breakpoint value in pixels.
   *
   * The breakpoint determines when the carousel switches from desktop to mobile
   * configuration. Default is 768px. Override this method to customize the mobile
   * breakpoint for shops with specific responsive design requirements.
   *
   * @returns The breakpoint width in pixels where mobile layout activates
   *
   * @example
   * Override to use custom breakpoint for shop theme:
   * ```typescript
   * protected getMobileBreakpoint() {
   *   // Match shop's theme breakpoint
   *   return 992; // Bootstrap's md breakpoint
   * }
   * ```
   */
  protected getMobileBreakpoint(): number {
    return this.MOBILE_BREAKPOINT;
  }

  /**
   * Gets the Slick carousel configuration for desktop view.
   *
   * Configures carousel behavior including slides to show/scroll, navigation arrows,
   * dots, and responsive breakpoints. The configuration respects RTL (right-to-left)
   * layout direction from the document. Override this method to customize carousel
   * behavior for specific shops or add features like autoplay.
   *
   * @param model - The recommendation model containing widget design settings
   *
   * @returns Slick carousel configuration object with desktop and responsive settings
   *
   * @remarks
   * The default configuration:
   * - Shows/scrolls number of products based on widget settings (default 4)
   * - Enables dots navigation, disables arrows
   * - Enables infinite scrolling and dragging
   * - Disables accessibility features for keyboard navigation (handled separately)
   * - Includes mobile breakpoint with different settings
   * - Respects RTL layout direction
   *
   * @example
   * Override to add autoplay and customize for shop:
   * ```typescript
   * protected getCarouselConfig(model) {
   *   const config = super.getCarouselConfig(model);
   *   return {
   *     ...config,
   *     autoplay: true,
   *     autoplaySpeed: 5000,
   *     pauseOnHover: true,
   *     arrows: true // Show navigation arrows
   *   };
   * }
   * ```
   *
   * @example
   * Override to customize slides for shop layout:
   * ```typescript
   * protected getCarouselConfig(model) {
   *   return {
   *     ...super.getCarouselConfig(model),
   *     slidesToShow: 3, // Always show 3 products
   *     slidesToScroll: 1, // Scroll one at a time
   *     centerMode: true,
   *     centerPadding: '60px'
   *   };
   * }
   * ```
   */
  protected getCarouselConfig(model: RecommendationModel): SlickOptions {
    const { widgetDesignSettings = {} } = model.properties;
    const isRtl = document.documentElement.getAttribute('dir') === 'rtl';

    return {
      dots: true,
      infinite: true,
      slidesToShow: widgetDesignSettings.numberOfProductPerRow || 4,
      slidesToScroll: widgetDesignSettings.numberOfProductPerRow || 4,
      arrows: false,
      draggable: true,
      useTransform: false,
      focusOnSelect: false,
      focusOnChange: false,
      accessibility: false,
      responsive: [
        {
          breakpoint: this.getMobileBreakpoint(),
          settings: this.getMobileCarouselConfig(),
        },
      ],
      rtl: isRtl,
    };
  }

  /**
   * Gets the Slick carousel configuration for mobile view.
   *
   * Defines mobile-specific carousel behavior when viewport width is below the mobile
   * breakpoint. Override this method to customize mobile carousel experience for
   * specific shops or device requirements.
   *
   * @returns Partial Slick carousel configuration for mobile devices
   *
   * @remarks
   * The default mobile configuration:
   * - Shows 2 slides at a time
   * - Scrolls 2 slides per interaction
   * - Enables dots navigation
   * - Enables infinite scrolling and dragging
   * - Disables accessibility features (handled separately)
   *
   * @example
   * Override to show single slide on mobile:
   * ```typescript
   * protected getMobileCarouselConfig() {
   *   return {
   *     ...super.getMobileCarouselConfig(),
   *     slidesToShow: 1,
   *     slidesToScroll: 1,
   *     dots: false // Hide dots for single slide
   *   };
   * }
   * ```
   *
   * @example
   * Override to enable vertical scrolling on mobile:
   * ```typescript
   * protected getMobileCarouselConfig() {
   *   return {
   *     dots: false,
   *     vertical: true,
   *     verticalSwiping: true,
   *     slidesToShow: 2,
   *     slidesToScroll: 1
   *   };
   * }
   * ```
   */
  protected getMobileCarouselConfig(): Partial<SlickOptions> {
    return {
      dots: true,
      infinite: true,
      slidesToShow: 2,
      slidesToScroll: 2,
      arrows: false,
      draggable: true,
      focusOnSelect: false,
      focusOnChange: false,
      accessibility: false,
    };
  }

  /**
   * Initializes and configures the Slick carousel with event handlers and accessibility features.
   *
   * This method handles the complete carousel setup including:
   * - Initializing Slick carousel with configuration
   * - Setting up RTL (right-to-left) layout if needed
   * - Configuring keyboard navigation with proper tabindex management
   * - Attaching carousel navigation button handlers
   * - Managing focus states for accessibility
   *
   * @param model - The recommendation model containing widget configuration
   * @param container - The HTML element containing the carousel
   *
   * @remarks
   * The carousel implements custom accessibility features:
   * - Updates tabindex for slides based on visibility
   * - Sets aria-label for each slide indicating position
   * - Syncs focus after slide changes
   * - Manages focusable elements within slides
   *
   * Performance note: This method uses jQuery and Slick carousel which are
   * lazy-loaded only when carousel layout is needed.
   *
   * @example
   * Override to add custom carousel event tracking:
   * ```typescript
   * applyRecommendationCarouselEvent(model, container) {
   *   super.applyRecommendationCarouselEvent(model, container);
   *
   *   // Track carousel interactions
   *   const slider = window.$appJQuery('.boost-sd__slider-container');
   *   slider.on('afterChange', (event, slick, currentSlide) => {
   *     this.analytics.track('carousel_slide_change', {
   *       widgetId: model.properties.widgetId,
   *       slideIndex: currentSlide
   *     });
   *   });
   * }
   * ```
   */
  applyRecommendationCarouselEvent(model: RecommendationModel, container: HTMLElement) {
    const { widgetId } = model.properties;
    const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
    // Get carousel configuration
    const carouselConfig = this.getCarouselConfig(model);

    // Init carousel
    const slider = (
      window.$appJQuery('.boost-sd__slider-container').not('.slick-initialized') as SlickInstance
    ).slick(carouselConfig);

    if (isRtl) {
      window.$appJQuery('.boost-sd__slider-container').css({
        direction: 'rtl',
      });

      window.$appJQuery('.boost-sd__slider-container .slick-slide').css({
        float: 'right',
      });
    }

    /**
     * updateTabindex() is the function to improve the behavior when navigate carousel by keyboard
     * Updates the tabindex and aria-label attributes of the slider container elements.
     *
     * Specifically, this function:
     * - Sets the aria-label attribute for each non-cloned slide to indicate its position (e.g. "Slide 1 of 5").
     * - Sets the tabindex attribute to 0 for the currently active slide and its focusable child elements.
     * - Sets the tabindex attribute to -1 for all other slides and their focusable child elements.
     *
     * This ensures that the slider is accessible and can be navigated using keyboard navigation.
     */
    const updateTabindex = () => {
      const items = container.querySelectorAll('.boost-sd__slider-container .slick-slide');
      const selectors = 'a[href], button:not([disabled]), .boost-sd__radio-label[tabindex]';
      const totalSlides = [...items].filter(
        (item) => !item.classList.contains('slick-cloned')
      ).length;
      let indexOfActiveSlide = 0;

      items.forEach((item) => {
        if (!(item instanceof HTMLElement)) return;
        if (!item.classList.contains('slick-cloned')) {
          indexOfActiveSlide++;
          item.setAttribute('aria-label', `Slide ${indexOfActiveSlide} of ${totalSlides}`);
        }

        const tabIndex = item.classList.contains('slick-active') ? 0 : -1;
        item.tabIndex = tabIndex;
        item.querySelectorAll(selectors).forEach((element) => {
          if (element instanceof HTMLElement) {
            element.tabIndex = tabIndex;
          }
        });
      });
    };

    const syncSlideFocusAfterChange = () => {
      updateTabindex();
      setTimeout(() => {
        window.$appJQuery(`#${widgetId} .slick-current`).trigger('focus');
      }, 400);
    };

    updateTabindex();

    slider.on('afterChange', syncSlideFocusAfterChange);

    this.attachCarouselButtonHandlers(container, slider);
  }

  /**
   * Attaches click event handlers to carousel navigation buttons (Previous/Next).
   *
   * Binds click events to circular navigation buttons based on their aria-label
   * attributes. Override this method to customize button behavior, add additional
   * navigation controls, or implement custom navigation logic.
   *
   * @param container - The HTML element containing the carousel and buttons
   * @param slider - The initialized Slick carousel instance
   *
   * @example
   * Override to add keyboard shortcuts for navigation:
   * ```typescript
   * protected attachCarouselButtonHandlers(container, slider) {
   *   super.attachCarouselButtonHandlers(container, slider);
   *
   *   // Add keyboard navigation
   *   container.addEventListener('keydown', (e) => {
   *     if (e.key === 'ArrowLeft') {
   *       slider.slick('slickPrev');
   *     } else if (e.key === 'ArrowRight') {
   *       slider.slick('slickNext');
   *     }
   *   });
   * }
   * ```
   *
   * @example
   * Override to add analytics tracking to navigation:
   * ```typescript
   * protected attachCarouselButtonHandlers(container, slider) {
   *   const buttons = container.querySelectorAll('.boost-sd__button--circle');
   *   buttons.forEach((button) => {
   *     button.addEventListener('click', () => {
   *       const direction = button.getAttribute('aria-label');
   *       this.analytics.track('carousel_navigation', { direction });
   *
   *       if (direction === 'Next') {
   *         slider.slick('slickNext');
   *       } else if (direction === 'Previous') {
   *         slider.slick('slickPrev');
   *       }
   *     });
   *   });
   * }
   * ```
   */
  protected attachCarouselButtonHandlers(container: HTMLElement, slider: SlickInstance): void {
    const carouselButtons = container.querySelectorAll('.boost-sd__button--circle');
    carouselButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        const behavior = button.getAttribute('aria-label');
        if (behavior === 'Next') {
          slider.slick('slickNext');
        } else if (behavior === 'Previous') {
          slider.slick('slickPrev');
        }
      });
    });
  }

  /**
   * Renders the carousel widget by loading dependencies and initializing the carousel.
   *
   * This method handles the complete carousel rendering process:
   * 1. Lazy loads jQuery library with noConflict mode
   * 2. Lazy loads Slick carousel library
   * 3. Renders widget template with product data
   * 4. Initializes carousel with event handlers
   *
   * @param container - The HTML element where the carousel will be rendered
   *
   * @throws {Error} When jQuery/Slick loading fails, template rendering fails,
   * or carousel initialization encounters errors
   *
   * @remarks
   * Performance considerations:
   * - jQuery (~30KB gzipped) and Slick (~15KB gzipped) are loaded on-demand only for carousel layouts
   * - Uses noConflict mode to avoid conflicts with page's jQuery instance
   * - Template rendering is handled by AppService for consistency
   * - Total additional bundle size: ~45KB gzipped when carousel layout is used
   *
   * @example
   * Override to add custom carousel initialization logic:
   * ```typescript
   * async renderCarouselWidget(container) {
   *   // Add loading indicator
   *   container.innerHTML = '<div class="loading">Loading carousel...</div>';
   *
   *   await super.renderCarouselWidget(container);
   *
   *   // Add custom post-render effects
   *   this.addCarouselAnimations(container);
   * }
   * ```
   */
  async renderCarouselWidget(container: HTMLElement) {
    const jquery = await import('jquery');
    const $appJQuery = jquery.default.noConflict(true);
    window.$appJQuery = $appJQuery;

    await import('slick-carousel');

    const { model } = this.state.value;
    if (model && this.template) {
      const taeConfig = this.appService.TAEAppConfig;
      const html = await this.appService.templateRender(this.template, {
        ...model.properties,
        request: {
          device: detectDeviceByWidth(),
          money_format: taeConfig.shop?.money_format,
          money_format_with_currency: taeConfig.shop?.money_format_with_currency,
        },
      });

      container.innerHTML = html;
      this.applyRecommendationCarouselEvent(model, container);
    }
  }
}

/**
 * Configuration properties for initializing the RecommendationWidgetController.
 */
export type RecommendationWidgetControllerProps = {
  /**
   * The unique identifier of the widget to initialize.
   * This ID is used to fetch widget configuration and recommendation data from the API.
   */
  widgetId: string;
};

/**
 * Internal state management for the recommendation widget controller.
 */
type RecommendationWidgetControllerState = {
  /**
   * Indicates whether the widget is currently fetching recommendation data.
   */
  loading: boolean;

  /**
   * The recommendation model containing widget configuration and product data.
   * Null when data hasn't been fetched yet or when fetch fails.
   */
  model: RecommendationModel | null;
};

/**
 * Slick carousel configuration options.
 *
 * Defines the behavior and appearance of the Slick carousel used for
 * recommendation widgets with carousel layout. For complete options reference,
 * see: https://kenwheeler.github.io/slick/
 */
interface SlickOptions {
  /** Whether to show dot indicators for navigation */
  dots?: boolean;

  /** Whether the carousel should loop infinitely */
  infinite?: boolean;

  /** Number of slides to show at once */
  slidesToShow?: number;

  /** Number of slides to scroll at once */
  slidesToScroll?: number;

  /** Whether to show previous/next arrow buttons */
  arrows?: boolean;

  /** Whether the carousel can be dragged with mouse */
  draggable?: boolean;

  /** Whether to use CSS3 transforms for slide transitions */
  useTransform?: boolean;

  /** Whether clicking a slide focuses it */
  focusOnSelect?: boolean;

  /** Whether to focus slides when they change */
  focusOnChange?: boolean;

  /** Whether to enable accessibility keyboard navigation */
  accessibility?: boolean;

  /** Responsive breakpoint configurations */
  responsive?: Array<{
    /** The breakpoint width in pixels */
    breakpoint: number;
    /** Settings to apply when below this breakpoint */
    settings: Partial<SlickOptions>;
  }>;

  /** Whether to enable right-to-left layout direction */
  rtl?: boolean;
}

/**
 * Slick carousel instance interface for type-safe method calls.
 *
 * Provides type definitions for interacting with an initialized Slick carousel.
 * Represents the jQuery plugin instance returned by $('.element').slick().
 */
interface SlickInstance {
  /**
   * Executes a Slick carousel method.
   *
   * @param options - Either a configuration object for initialization, or a method name string
   * (e.g., 'slickNext', 'slickPrev', 'slickGoTo')
   * @param args - Additional arguments for the specified method (e.g., slide index for 'slickGoTo')
   * @returns The Slick instance for method chaining
   */
  slick(options: SlickOptions | string, ...args: unknown[]): SlickInstance;

  /**
   * Attaches an event handler to the carousel.
   *
   * @param event - The event name (e.g., 'afterChange', 'beforeChange')
   * @param handler - The event handler function to execute
   */
  on(event: string, handler: () => void): void;
}
